/**
 * Layout configuration types for GlobalAppLayout
 * Data-driven approach - stores serializable data instead of React elements
 */

import { AppLayoutProps } from '@amzn/awsui-components-core';
import { ReactNode } from 'react';

/**
 * Navigation types - identifies which navigation component to render
 */
export type NavigationType = 'home' | 'manage-programs' | 'none';

/**
 * Breadcrumb item data
 */
export interface BreadcrumbItem {
  text: string;
  href?: string;
}

/**
 * Sticky header configuration for pages with dynamic headers
 */
export interface StickyHeaderConfig {
  /** Whether sticky header is enabled */
  enabled: boolean;
  /** The sticky header component - only used when enabled */
  component?: ReactNode;
  /** Breadcrumbs to show in sticky header area */
  breadcrumbs?: BreadcrumbItem[];
}

/**
 * Flashbar item type - matches Cloudscape FlashbarProps.MessageDefinition
 */
export interface FlashbarItem {
  id?: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'in-progress';
  loading?: boolean;
  content?: ReactNode;
  dismissible?: boolean;
  dismissLabel?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDismiss?: any;
}

/**
 * Split panel configuration
 */
export interface SplitPanelConfig {
  /** The split panel component */
  component?: ReactNode;
  /** Whether the split panel is open */
  open?: boolean;
  /** Split panel preferences */
  preferences?: AppLayoutProps.SplitPanelPreferences;
  /** Split panel size */
  size?: number;
}

/**
 * Tools panel configuration
 */
export interface ToolsPanelConfig {
  /** The tools panel component */
  component?: ReactNode;
  /** Whether the tools panel is hidden */
  hide?: boolean;
  /** Whether the tools panel is open */
  open?: boolean;
  /** Width of the tools panel */
  width?: number;
  /** Callback when tools panel open state changes */
  onChange?: (event: { detail: { open: boolean } }) => void;
}

/**
 * Configuration interface for AppLayout
 * Uses data-driven approach for better performance and simpler state management
 */
export interface LayoutConfig {
  /** Navigation type - determines which navigation component to render */
  navigationType: NavigationType;

  /** Whether the navigation panel is hidden */
  navigationHide: boolean;

  /** Breadcrumb items data */
  breadcrumbs: BreadcrumbItem[];

  /** Sticky header configuration */
  stickyHeader: StickyHeaderConfig;

  /** Additional flashbar items from the current page */
  pageFlashbarItems: FlashbarItem[];

  /** Tools panel configuration */
  tools: ToolsPanelConfig;

  /** Split panel configuration */
  splitPanel: SplitPanelConfig;

  /** Content type for the layout */
  contentType: 'default' | 'table' | 'form' | 'wizard' | 'cards';

  /** Maximum content width */
  maxContentWidth: number;
}

/**
 * Partial config for updates - allows setting only specific fields
 */
export type LayoutConfigUpdate = Partial<LayoutConfig>;

/**
 * Context value interface for LayoutContext
 */
export interface LayoutContextValue {
  /** Current layout configuration */
  config: LayoutConfig;

  /** Whether the navigation drawer is currently open (not hidden, but expanded) */
  navigationOpen: boolean;

  /** Function to update layout configuration (merges with existing) */
  setConfig: (config: LayoutConfigUpdate) => void;

  /** Function to reset layout configuration to defaults */
  resetConfig: () => void;

  /** Function to update navigation drawer open state */
  setNavigationOpen: (open: boolean) => void;
}

/**
 * Default layout configuration
 */
export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  navigationType: 'home',
  navigationHide: false,
  breadcrumbs: [{ text: 'Home', href: '/' }],
  stickyHeader: {
    enabled: false,
    component: undefined,
    breadcrumbs: undefined,
  },
  pageFlashbarItems: [],
  tools: {
    component: undefined,
    hide: true,
    open: false,
    width: undefined,
    onChange: undefined,
  },
  splitPanel: {
    component: undefined,
    open: false,
    preferences: { position: 'side' },
    size: 750,
  },
  contentType: 'default',
  maxContentWidth: Number.MAX_VALUE,
};
